import { PortalFXSwapsTableProps } from '@/components/portal-modules/PortalFXSwapsTable';
import { defaultColumn } from '@/config/config';
import { FXSwapRow, getFxSwapColumns } from '@/helpers/portalFXSwapsHelper';
import { useAppSelector } from '@/state/hooks';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalFXSwapsRow extends FXSwapRow {}

interface UsePortalFXSwapsTableProps extends PortalFXSwapsTableProps {}

const usePortalFXSwapsTable = (props: UsePortalFXSwapsTableProps) => {
    const { data, isLoading } = props;

    const pagination = useAppSelector(selectTablePagination);

    const lang = useLanguage();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const columns: ColumnDef<FXSwapRow>[] = useMemo(() => {
        if (data) return getFxSwapColumns({ lang });
        else return [];
    }, [lang, data, isLoading]);

    const formattedPortalFXSwaps: PortalFXSwapsRow[] = useMemo(() => {
        if (data) return data;
        return [];
    }, [data]);

    const tableInstance = useReactTable({
        data: formattedPortalFXSwaps,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: formattedPortalFXSwaps ? Math.ceil(formattedPortalFXSwaps.length / pagination.pageSize) : undefined,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columns, columnSizing, sorting, formattedPortalFXSwaps, globalFilter };
};

export default usePortalFXSwapsTable;
